import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Skeleton } from "primereact/skeleton";
import React from "react";
import EmptyIcon from "../../../assets/empty-file.svg";
import EmptyDataView from "./EmptyDataView";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { currencyList } from "~/utils/currency";
import Menu from "~/components/menu/Menu";
import AdminOnly from "~/components/AdminOnly";

const ShareRegistryTable = ({
  loading,
  shareRegistryData,
  setModalVisible,
  formatDate,
  editTransactionHandler,
  onPageChange,
  pageSize,
  page,
  totalRecords,
  isEditing,
  onDeleteTransaction,
}) => {
  const skeletonRows = [...Array(5)];
  const { isAdmin } = useSelector((state) => state.login);

  const actionMenuItems = (data) => [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => editTransactionHandler?.(data),
    },
    {
      label: "Delete Transaction",
      icon: "pi pi-trash",
      command: () => onDeleteTransaction?.(data),
    },
  ];

  return (
    <div className="table-container">
      {!loading && shareRegistryData.length === 0 ? (
        <div className="flex flex-column justify-content-center h-full">
          <EmptyDataView
            Icon={<img src={EmptyIcon} alt="Empty File Icon" />}
            Header="No share transactions have been recorded."
            Description="Begin by registering a new transaction."
            setModalVisible={setModalVisible}
          />
        </div>
      ) : (
        <DataTable
          value={loading ? skeletonRows : shareRegistryData}
          className="datatable-right-align"
          lazy
          {...(isEditing && {
            paginator: totalRecords > 10,
            first: isEditing ? page * pageSize : 0,
            rows: pageSize,
            totalRecords: totalRecords,
            onPage: onPageChange,
            rowsPerPageOptions: [10, 20, 30],
          })}
          emptyMessage="No results were found"
        >
          <Column
            header="Transaction Type"
            body={(data) => (loading ? <Skeleton width="100%" /> : data.type)}
          />
          <Column
            header="Transaction Date"
            body={(data) =>
              loading ? (
                <Skeleton width="100%" />
              ) : (
                formatDate(data?.acquisition_date)
              )
            }
          />
          <Column
            header="# Of Shares"
            body={(data) =>
              loading ? (
                <Skeleton width="100%" />
              ) : (
                <span>{data?.number_of_shares}</span>
              )
            }
          />
          <Column
            header="Share Value"
            body={(rowData) => {
              if (loading) return <Skeleton width="100%" />;
              const currencyCode = rowData?.currency;
              const currency = currencyList.find(
                (c) => c.value === currencyCode,
              );
              const symbol = currency?.symbol;
              return (
                <span>
                  {symbol} {rowData?.share_value}
                </span>
              );
            }}
          />
          <Column
            header="Share %"
            body={(data) =>
              loading ? (
                <Skeleton width="100%" />
              ) : (
                <span>{data?.share_percentage}</span>
              )
            }
          />
          <Column
            header="Share Class"
            body={(data) =>
              loading ? (
                <Skeleton width="100%" />
              ) : (
                <span>{data?.share_class}</span>
              )
            }
          />
          <Column
            hidden={!isAdmin}
            body={(data) =>
              loading ? (
                <Skeleton width="100%" />
              ) : (
                <AdminOnly>
                  <div className="flex align-items-center justify-content-end gap-0">
                    <Menu menuItems={actionMenuItems(data)} />
                  </div>
                </AdminOnly>
              )
            }
          />
        </DataTable>
      )}
    </div>
  );
};

ShareRegistryTable.propTypes = {
  loading: PropTypes.bool,
  shareRegistryData: PropTypes.array,
  setModalVisible: PropTypes.func,
  formatDate: PropTypes.func,
  editTransactionHandler: PropTypes.func,
  onPageChange: PropTypes.func,
  pageSize: PropTypes.number,
  page: PropTypes.number,
  totalRecords: PropTypes.number,
  isEditing: PropTypes.bool,
  onDeleteTransaction: PropTypes.func,
};

export default ShareRegistryTable;
